/**
 * A labelled form field.
 *
 * Real <label for> rather than a placeholder: placeholders vanish on focus,
 * fail contrast, and are not announced reliably. The audience includes people
 * on phones in daylight and people using text scaling (Decision #18).
 */
import { useId } from 'react';

/**
 * `required` and `optional` are two different questions, and the course-section
 * builder is where that stopped being academic. A draft section is saved
 * half-finished on purpose, so almost nothing may be HTML-`required` — but the
 * CCL code is not *optional* either, it is required to publish. Labelling it
 * "(optional)" would be a lie, and blocking the save would defeat drafts.
 *
 * So: `required` controls the browser's validation, `optional` controls the
 * label. `optional` defaults to `!required`, which is what every earlier call
 * site already assumed.
 */
export function Field({
  label,
  type = 'text',
  value,
  onChange,
  error,
  hint,
  autoComplete,
  required = true,
  optional,
  disabled = false,
  ...rest
}) {
  const id = useId();
  const showOptional = optional ?? !required;
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="field">
      <label htmlFor={id}>
        {label}
        {showOptional && <span className="muted"> (optional)</span>}
      </label>

      {hint && (
        <p className="field__hint" id={hintId}>
          {hint}
        </p>
      )}

      <input
        id={id}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required={required}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={error ? 'true' : undefined}
        {...rest}
      />

      {error && (
        <p className="field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export default Field;
